<template>
  <v-container fluid class="pa-4 pa-md-6">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div>
            <h1 class="dashboard-title">An&aacute;lisis de Ventas</h1>
            <p class="text-caption grey--text">Tendencias y comparativo de ventas por periodo</p>
          </div>
        </div>
        <PeriodSelector :loading="loading" default-preset="30d" @change="onPeriodChange" class="mt-2" />
      </v-col>
    </v-row>

    <!-- KPI Cards with Deltas -->
    <v-row class="mb-4">
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="Ventas Netas" :value="data.ventas" :previous-value="prev.ventas" format="currency" icon="mdi-cash-register" gradient="success-gradient" />
      </v-col>
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="Ticket Promedio" :value="data.ticket_promedio" :previous-value="prev.ticket_promedio" format="currency" icon="mdi-receipt" gradient="primary-gradient" />
      </v-col>
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="Unidades" :value="data.unidades" :previous-value="prev.unidades" format="number" icon="mdi-package-variant" gradient="warning-gradient" />
      </v-col>
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="Tasa Descuento" :value="data.tasa_descuento" :previous-value="prev.tasa_descuento" format="percent" icon="mdi-tag-outline" gradient="info-gradient" :invert-delta="true" />
      </v-col>
    </v-row>

    <!-- Dual-Line Chart: Ventas por Día -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="primary">mdi-chart-line</v-icon>
            Ventas por D&iacute;a
            <v-spacer></v-spacer>
            <span class="text-caption grey--text">Azul: periodo actual &bull; Gris: periodo anterior</span>
          </v-card-title>
          <v-card-text>
            <GChart
              v-if="dualLineChart.length > 1"
              type="LineChart"
              :data="dualLineChart"
              :options="dualLineOptions"
              class="chart-container"
            />
            <div v-else class="text-center grey--text pa-6">Sin datos para graficar</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-4">
      <!-- Ventas por categoría -->
      <v-col cols="12" md="6">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="success">mdi-shape</v-icon>
            Ventas por Categor&iacute;a
          </v-card-title>
          <v-card-text>
            <GChart
              v-if="salesByCategoryChart.length > 1"
              type="ColumnChart"
              :data="salesByCategoryChart"
              :options="barChartOptions"
              class="chart-container"
            />
            <div v-else class="text-center grey--text pa-6">Sin datos</div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Top 10 marcas -->
      <v-col cols="12" md="6">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="warning">mdi-tag-multiple</v-icon>
            Top 10 Marcas
          </v-card-title>
          <v-card-text>
            <GChart
              v-if="salesByBrandChart.length > 1"
              type="BarChart"
              :data="salesByBrandChart"
              :options="hBarChartOptions"
              class="chart-container"
            />
            <div v-else class="text-center grey--text pa-6">Sin datos</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Sales Mix -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="teal">mdi-chart-pie</v-icon>
            Sales Mix por Categor&iacute;a
            <v-spacer></v-spacer>
            <span class="text-caption grey--text">Participaci&oacute;n y cambio vs periodo anterior</span>
          </v-card-title>
          <v-data-table
            v-if="!isMobile"
            :headers="salesMixHeaders"
            :items="salesMixData"
            :loading="loading"
            dense
            hide-default-footer
            :items-per-page="20"
            class="analyst-table"
            no-data-text="Sin datos"
          >
            <template v-slot:item.ventas="{ item }">{{ formatCurrency(item.ventas) }}</template>
            <template v-slot:item.ventas_prev="{ item }">{{ formatCurrency(item.ventas_prev) }}</template>
            <template v-slot:item.delta_pct="{ item }">
              <span :class="item.delta_pct >= 0 ? 'success--text' : 'error--text'">
                <v-icon x-small :color="item.delta_pct >= 0 ? 'success' : 'error'">{{ item.delta_pct >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                {{ Math.abs(item.delta_pct).toFixed(1) }}%
              </span>
            </template>
            <template v-slot:item.share="{ item }">{{ item.share.toFixed(1) }}%</template>
            <template v-slot:item.share_delta="{ item }">
              <span :class="item.share_delta >= 0 ? 'success--text' : 'error--text'">
                {{ item.share_delta >= 0 ? '+' : '' }}{{ item.share_delta.toFixed(1) }}pp
              </span>
            </template>
          </v-data-table>
          <v-list v-else dense class="pa-0">
            <div v-if="loading" class="text-center pa-4"><v-progress-circular indeterminate size="24"></v-progress-circular></div>
            <div v-else-if="!salesMixData.length" class="text-center grey--text pa-4">Sin datos</div>
            <template v-else>
              <v-list-item v-for="(item, i) in salesMixData" :key="i" class="mobile-item">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ item.categoria }}</v-list-item-title>
                  <v-list-item-subtitle>{{ formatCurrency(item.ventas) }} &bull; {{ item.share.toFixed(1) }}% share</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="text-right ma-0">
                  <span :class="item.delta_pct >= 0 ? 'success--text' : 'error--text'" class="text-body-2 font-weight-bold">
                    <v-icon x-small :color="item.delta_pct >= 0 ? 'success' : 'error'">{{ item.delta_pct >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                    {{ Math.abs(item.delta_pct).toFixed(1) }}%
                  </span>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <!-- Vendor Scorecard -->
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="purple">mdi-account-group</v-icon>
            Scorecard Vendedores
          </v-card-title>
          <v-data-table
            v-if="!isMobile"
            :headers="vendorHeaders"
            :items="vendorScorecard"
            :loading="loading"
            dense
            hide-default-footer
            :items-per-page="20"
            class="analyst-table"
            no-data-text="Sin datos para el periodo"
          >
            <template v-slot:item.ventas="{ item }">{{ formatCurrency(item.ventas) }}</template>
            <template v-slot:item.atv="{ item }">{{ formatCurrency(item.atv) }}</template>
            <template v-slot:item.delta_pct="{ item }">
              <span v-if="item.delta_pct !== null" :class="item.delta_pct >= 0 ? 'success--text' : 'error--text'">
                <v-icon x-small :color="item.delta_pct >= 0 ? 'success' : 'error'">{{ item.delta_pct >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                {{ Math.abs(item.delta_pct).toFixed(1) }}%
              </span>
              <span v-else class="grey--text">Nuevo</span>
            </template>
          </v-data-table>
          <v-list v-else dense class="pa-0">
            <div v-if="loading" class="text-center pa-4"><v-progress-circular indeterminate size="24"></v-progress-circular></div>
            <div v-else-if="!vendorScorecard.length" class="text-center grey--text pa-4">Sin datos para el periodo</div>
            <template v-else>
              <v-list-item v-for="(item, i) in vendorScorecard" :key="i" class="mobile-item">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ item.vendedor }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.tickets }} tickets &bull; ATV: {{ formatCurrency(item.atv) }} &bull; UPT: {{ item.upt }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="text-right ma-0">
                  <div class="text-body-2 font-weight-bold">{{ formatCurrency(item.ventas) }}</div>
                  <span v-if="item.delta_pct !== null" :class="item.delta_pct >= 0 ? 'success--text' : 'error--text'" class="text-caption">
                    {{ item.delta_pct >= 0 ? '+' : '' }}{{ item.delta_pct.toFixed(1) }}%
                  </span>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { webserver, formatCurrency } from "@/services/webserver.js";
import { GChart } from "vue-google-charts";
import PeriodSelector from "@/components/PeriodSelector.vue";
import KpiDeltaCard from "@/components/KpiDeltaCard.vue";

export default {
  name: "SalesAnalysis",
  components: { GChart, PeriodSelector, KpiDeltaCard },
  data() {
    return {
      loading: false,
      period: {},
      data: {
        ventas: 0, costo: 0, transacciones: 0, unidades: 0, descuentos: 0,
        ticket_promedio: 0, upt: 0, tasa_descuento: 0,
        sales_by_day: [], sales_by_day_prev: [],
        sales_by_category: [],
        sales_by_brand: [], sales_by_vendor: [],
        prev: null,
      },
      salesMixHeaders: [
        { text: "Categor\u00eda", value: "categoria", sortable: false },
        { text: "Ventas Actual", value: "ventas", align: "end" },
        { text: "Ventas Anterior", value: "ventas_prev", align: "end" },
        { text: "\u0394%", value: "delta_pct", align: "center" },
        { text: "% Share", value: "share", align: "end" },
        { text: "\u0394 Share", value: "share_delta", align: "center" },
      ],
      vendorHeaders: [
        { text: "Vendedor", value: "vendedor", sortable: false },
        { text: "Tickets", value: "tickets", align: "center" },
        { text: "Unidades", value: "unidades", align: "center" },
        { text: "Ventas", value: "ventas", align: "end" },
        { text: "ATV", value: "atv", align: "end" },
        { text: "UPT", value: "upt", align: "center" },
        { text: "\u0394% Ventas", value: "delta_pct", align: "center" },
      ],
      dualLineOptions: {
        legend: { position: "none" },
        curveType: "function",
        colors: ["#1a73e8", "#dadce0"],
        chartArea: { width: "85%", height: "75%" },
        hAxis: { textStyle: { fontSize: 10 }, slantedText: true },
        vAxis: { textStyle: { fontSize: 11 }, format: "short" },
        series: { 0: { lineWidth: 3 }, 1: { lineWidth: 2, lineDashStyle: [4, 4] } },
        height: 300,
      },
      barChartOptions: {
        legend: { position: "none" },
        colors: ["#34a853"],
        chartArea: { width: "80%", height: "75%" },
        hAxis: { textStyle: { fontSize: 10 }, slantedText: true },
        vAxis: { format: "short" },
        height: 300,
      },
      hBarChartOptions: {
        legend: { position: "none" },
        colors: ["#f9ab00"],
        chartArea: { width: "60%", height: "80%" },
        hAxis: { format: "short" },
        vAxis: { textStyle: { fontSize: 11 } },
        height: 350,
      },
    };
  },
  computed: {
    isMobile() { return this.$vuetify.breakpoint.xs; },
    prev() {
      return this.data.prev || {
        ventas: null, ticket_promedio: null, unidades: null, tasa_descuento: null
      };
    },
    dualLineChart() {
      const current = this.data.sales_by_day || [];
      const prev = this.data.sales_by_day_prev || [];
      if (!current.length) return [["D\u00eda", "Actual", "Anterior"]];
      const maxLen = Math.max(current.length, prev.length);
      const rows = [];
      for (let i = 0; i < maxLen; i++) {
        const label = current[i] ? current[i].order_date : `D${i + 1}`;
        const curVal = current[i] ? parseFloat(current[i].ventas) : 0;
        const prevVal = prev[i] ? parseFloat(prev[i].ventas) : 0;
        rows.push([label, curVal, prevVal]);
      }
      return [["D\u00eda", "Actual", "Anterior"], ...rows];
    },
    salesByCategoryChart() {
      if (!this.data.sales_by_category.length) return [["Categor\u00eda", "Ventas"]];
      return [
        ["Categor\u00eda", "Ventas"],
        ...this.data.sales_by_category.map((d) => [d.categoria, parseFloat(d.ventas)])
      ];
    },
    salesByBrandChart() {
      if (!this.data.sales_by_brand.length) return [["Marca", "Ventas"]];
      return [
        ["Marca", "Ventas"],
        ...this.data.sales_by_brand.map((d) => [d.marca, parseFloat(d.ventas)])
      ];
    },
    salesMixData() {
      const rows = this.data.sales_by_category || [];
      const totalCurrent = rows.reduce((s, c) => s + parseFloat(c.ventas || 0), 0);
      const totalPrev = rows.reduce((s, c) => s + parseFloat(c.ventas_prev || 0), 0);
      return rows.map((c) => {
        const v = parseFloat(c.ventas || 0);
        const vp = parseFloat(c.ventas_prev || 0);
        const share = totalCurrent > 0 ? (v / totalCurrent) * 100 : 0;
        const sharePrev = totalPrev > 0 ? (vp / totalPrev) * 100 : 0;
        return {
          categoria: c.categoria,
          ventas: v,
          ventas_prev: vp,
          delta_pct: vp > 0 ? ((v - vp) / vp) * 100 : (v > 0 ? 100 : 0),
          share,
          share_delta: share - sharePrev,
        };
      });
    },
    vendorScorecard() {
      const rows = this.data.sales_by_vendor || [];
      return rows.map((v) => {
        const ventas = parseFloat(v.ventas || 0);
        const ventasPrev = parseFloat(v.ventas_prev || 0);
        return {
          vendedor: v.vendedor,
          tickets: v.tickets,
          unidades: v.unidades,
          ventas,
          atv: parseFloat(v.atv || 0),
          upt: parseFloat(v.upt || 0),
          delta_pct: ventasPrev > 0
            ? ((ventas - ventasPrev) / ventasPrev) * 100
            : (ventas > 0 ? 100 : 0),
        };
      });
    },
  },
  methods: {
    formatCurrency,
    onPeriodChange(period) {
      this.period = period;
      this.loadData();
    },
    loadData() {
      if (!this.period.dateFrom) return;
      this.loading = true;
      const store = this.$store.state.profile.store_id || window.store?.store_id;
      webserver("get_sales_analysis", {
        store,
        date_from: this.period.dateFrom,
        date_to: this.period.dateTo,
        compare_from: this.period.compareDateFrom,
        compare_to: this.period.compareDateTo,
      }, (res) => {
        this.loading = false;
        if (res && !res.error) {
          this.data = { ...this.data, ...res };
        }
      });
    },
  },
};
</script>

<style scoped>
.dashboard-title { font-size: 1.5rem; font-weight: 700; color: #424242; }
.section-title { font-size: 0.9375rem; font-weight: 600; color: #424242; }
.analyst-table { font-size: 0.8125rem; }
.chart-container { width: 100%; min-height: 280px; }
.mobile-item { min-height: 48px; border-bottom: 1px solid #f0f0f0; }
.mobile-item:last-child { border-bottom: none; }
</style>
